import type { AttributeFilters } from '@ipfabric/utils';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';

export interface AttributePolicyFormValues {
  name: string;
  description: string;
  attributeFilters: AttributeFilters;
  // fake input for filter error since formik is not able to type attributeFilters properly
  filterError?: string;
}

export const emptyAttributePolicyFormikValues: AttributePolicyFormValues = {
  name: '',
  description: '',
  attributeFilters: {},
};

export type UseAttributePolicyFormikHook =
  FormikProps<AttributePolicyFormValues>;

interface Props {
  initialValues: AttributePolicyFormValues;
  onSubmit: (values: AttributePolicyFormValues) => void;
}

export const useAttributePolicyFormik = (
  props: Props,
): UseAttributePolicyFormikHook => {
  const {
    administrationPolicies: { form: policiesFormMsg },
  } = useLocale();
  const formik = useFormik<AttributePolicyFormValues>({
    initialValues: props.initialValues,
    validate: (values) => {
      const errors: FormikErrors<AttributePolicyFormValues> = {};
      if (!values.name) {
        errors.name = policiesFormMsg.validations.requiredPolicyName;
      }

      if (!Object.values(values.attributeFilters).some((v) => v.length > 0)) {
        errors.filterError =
          policiesFormMsg.validations.requiredNonEmptyAttribute;
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values) => {
      const valuesToSubmit = {
        name: values.name,
        description: values.description,
        attributeFilters: Object.entries(
          values.attributeFilters,
        ).reduce<AttributeFilters>((acc, [key, value]) => {
          if (value.length > 0) {
            acc[key] = value;
          }

          return acc;
        }, {}),
      };

      props.onSubmit(valuesToSubmit);
    }),
    enableReinitialize: true,
  });

  return formik;
};
